import {
  Assessment as ResearchIcon,
  Cancel as RejectIcon,
  Delete as DeleteIcon,
  FileDownload as ExportIcon,
  WarningAmber,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  Snackbar,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  MaterialReactTable,
  MRT_ColumnDef,
  MRT_RowSelectionState,
} from "material-react-table";
import React, { useCallback, useMemo, useState } from "react";
import { useEntityExport } from "../../../hooks/useEntityExport";
import { ResearchEntity } from "../../../types/researchWorkflow";
import { getLabelDisplay } from "../../../utils/labelMapping";
import ExportFilterModal, {
  ExportFilter,
} from "../LongList/ExportFilterModal";
import DeepEvaluationModal from "../ShortList/DeepEvaluationModal";
import EvaluationScoreTooltip from "../ShortList/EvaluationScoreTooltip";
import { selectVisibleDeselectAllProps } from "../shared/selectionProps";

interface FinalSelectionViewProps {
  entities: ResearchEntity[];
  trackId: string;
  requirements: {
    criteria: Array<{
      id: string;
      label: string;
      weight: number;
    }>;
  };
  onRemoveFromFinal: (entityId: string) => void;
  onBulkDismiss: (entityIds: string[]) => void;
  onSelectEntity: (entityId: string) => void;
  onEntityClick?: (entityId: string) => void;
  isLoading?: boolean;
  isViewer?: boolean;
}

const FinalSelectionView: React.FC<FinalSelectionViewProps> = ({
  entities,
  trackId,
  requirements,
  onRemoveFromFinal,
  onBulkDismiss,
  onSelectEntity,
  onEntityClick,
  isLoading = false,
  isViewer = false,
}) => {
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [evaluationModalOpen, setEvaluationModalOpen] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [selectedEntityForEvaluation, setSelectedEntityForEvaluation] =
    useState<ResearchEntity | null>(null);

  const handleExport = (filter: ExportFilter) => {
    if (filter === "selection") {
      const selectedIds = Object.keys(rowSelection).filter(
        (key) => rowSelection[key],
      );
      exportToExcel(undefined, selectedIds);
      setExportModalOpen(false);
      return;
    }

    let decisionStates: string[] | undefined;
    switch (filter) {
      case "all":
        decisionStates = undefined;
        break;
      case "final":
        decisionStates = ["final"];
        break;
      default:
        decisionStates = [filter];
    }
    exportToExcel(decisionStates);
    setExportModalOpen(false);
  };

  const handleViewEvaluation = useCallback((entity: ResearchEntity) => {
    setSelectedEntityForEvaluation(entity);
    setEvaluationModalOpen(true);
  }, []);

  const {
    exportToExcel,
    isExporting,
    error: exportError,
    clearError,
  } = useEntityExport(trackId, "final", ["final"]);

  const finalEntities = useMemo(
    () => entities.filter((e) => e.workflowStatus === "final"),
    [entities],
  );

  const columns = useMemo<MRT_ColumnDef<ResearchEntity>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        size: 250,
        Cell: ({ cell, row }) => (
          <Typography
            variant="body2"
            fontWeight="600"
            sx={{
              cursor: "pointer",
              "&:hover": {
                color: "primary.light",
                textDecoration: "underline",
              },
            }}
            onClick={(e) => {
              e.stopPropagation();
              if (onEntityClick) {
                onEntityClick(row.original.kg_entity_id);
              } else {
                onSelectEntity(row.original.id);
              }
            }}
          >
            {String(cell.getValue())}
          </Typography>
        ),
      },
      {
        accessorKey: "tags",
        header: "Tags",
        size: 200,
        Cell: ({ cell }) => {
          const tags = cell.getValue() as string[] | undefined;
          if (!tags || tags.length === 0) return null;
          return (
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
              {tags.slice(0, 2).map((tag, idx) => {
                const labelInfo = getLabelDisplay(tag);
                return (
                  <Tooltip key={idx} title={labelInfo.full} arrow>
                    <Chip
                      label={labelInfo.abbrev}
                      size="small"
                      sx={{ fontSize: "0.7rem" }}
                    />
                  </Tooltip>
                );
              })}
              {tags.length > 2 && (
                <Tooltip
                  title={tags
                    .slice(2)
                    .map((t) => getLabelDisplay(t).full)
                    .join(", ")}
                  arrow
                >
                  <Chip
                    label={`+${tags.length - 2}`}
                    size="small"
                    sx={{ fontSize: "0.7rem" }}
                  />
                </Tooltip>
              )}
            </Box>
          );
        },
      },
      {
        id: "evaluationScore",
        accessorFn: (row) => row.evaluationData?.overall_score ?? -1,
        header: "Score",
        size: 80,
        Cell: ({ row }) => {
          const entity = row.original;
          const displayScore = entity.evaluationData?.overall_score;

          const getScoreChipColor = (
            score: number | undefined,
          ): { backgroundColor: string; color: string } => {
            if (score === undefined)
              return {
                backgroundColor: "action.disabledBackground",
                color: "text.disabled",
              };
            if (score >= 4.5)
              return {
                backgroundColor: "success.dark",
                color: "rgba(0,0,0,0.87)",
              };
            if (score >= 3.5)
              return {
                backgroundColor: "success.main",
                color: "rgba(0,0,0,0.87)",
              };
            if (score >= 2.5)
              return {
                backgroundColor: "warning.main",
                color: "rgba(0,0,0,0.87)",
              };
            if (score >= 1.5)
              return {
                backgroundColor: "error.main",
                color: "rgba(0,0,0,0.87)",
              };
            return {
              backgroundColor: "error.dark",
              color: "rgba(0,0,0,0.87)",
            };
          };

          const evaluationData = entity.evaluationData as any;

          const chip = (
            <Chip
              label={displayScore !== undefined ? displayScore.toFixed(1) : "-"}
              size="small"
              sx={{
                fontWeight: 600,
                ...getScoreChipColor(displayScore),
                ...(displayScore !== undefined && {
                  cursor: "pointer",
                  "&:hover": { opacity: 0.85 },
                }),
              }}
              onClick={
                displayScore !== undefined
                  ? (e) => {
                      e.stopPropagation();
                      handleViewEvaluation(entity);
                    }
                  : undefined
              }
            />
          );

          if (displayScore === undefined) return chip;

          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <EvaluationScoreTooltip evaluationData={evaluationData}>
                {chip}
              </EvaluationScoreTooltip>
              {evaluationData?.spec_version_changed && (
                <WarningAmber sx={{ fontSize: 16, color: "warning.main" }} />
              )}
            </Box>
          );
        },
      },
      {
        id: "actions",
        header: "Actions",
        size: 150,
        Cell: ({ row }) => {
          const entity = row.original;
          return (
            <Box sx={{ display: "flex", gap: 0.5 }}>
              {entity.deepResearchStatus === "complete" &&
                entity.evaluationData && (
                  <Tooltip title="View Evaluation Results">
                    <IconButton
                      size="small"
                      color="success"
                      onClick={() => handleViewEvaluation(entity)}
                    >
                      <ResearchIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              <Tooltip title="Remove from Final">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => onRemoveFromFinal(entity.id)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
    ],
    [onSelectEntity, onRemoveFromFinal, handleViewEvaluation],
  );

  const visibleColumns = isViewer
    ? columns.filter((col) => col.id !== "actions")
    : columns;

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Header */}
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight="600" gutterBottom>
            Final Selection
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Entities selected for the final research output
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          {!isViewer && (
            <Button
              variant="outlined"
              startIcon={<ExportIcon />}
              onClick={() => setExportModalOpen(true)}
              disabled={isExporting || finalEntities.length === 0}
              size="large"
            >
              {isExporting ? "Exporting..." : "Export to Excel"}
            </Button>
          )}
        </Box>
      </Box>

      {/* Bulk Actions Row */}
      {!isViewer &&
        Object.keys(rowSelection).filter((key) => rowSelection[key]).length >
          0 && (
          <Box
            sx={{
              mb: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 1.5,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {
                Object.keys(rowSelection).filter((key) => rowSelection[key])
                  .length
              }{" "}
              selected
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={<RejectIcon />}
              onClick={() => {
                const selectedIds = Object.keys(rowSelection).filter(
                  (key) => rowSelection[key],
                );
                onBulkDismiss(selectedIds);
                setRowSelection({});
              }}
            >
              Dismiss
            </Button>
          </Box>
        )}

      {/* Entity Table */}
      <Box sx={{ flex: 1, overflow: "hidden" }}>
        {finalEntities.length === 0 ? (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: 1,
              borderColor: "divider",
              borderRadius: 1,
              borderStyle: "dashed",
            }}
          >
            <Box sx={{ textAlign: "center", p: 4 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No final selections yet
              </Typography>
              <Typography variant="body2" color="text.disabled">
                Add entities from evaluation to finalize your selection
              </Typography>
            </Box>
          </Box>
        ) : (
          <MaterialReactTable
            columns={visibleColumns}
            data={finalEntities}
            enableColumnResizing
            enableStickyHeader
            enableBottomToolbar
            enableTopToolbar={false}
            enableRowSelection
            // Select adds the visible page; a fully-selected page clears the
            // whole id-keyed selection, including rows parked on other pages.
            muiSelectAllCheckboxProps={selectVisibleDeselectAllProps(
              setRowSelection,
            )}
            getRowId={(row) => row.id}
            onRowSelectionChange={setRowSelection}
            state={{
              isLoading: isLoading,
              showProgressBars: isLoading,
              rowSelection,
            }}
            initialState={{
              density: "compact",
              pagination: { pageSize: 20, pageIndex: 0 },
              sorting: [{ id: "evaluationScore", desc: true }],
            }}
            muiTablePaperProps={{
              elevation: 0,
              sx: {
                height: "100%",
                display: "flex",
                flexDirection: "column",
                backgroundColor: "background.paper",
              },
            }}
            muiTableContainerProps={{
              sx: { flex: 1 },
            }}
            muiBottomToolbarProps={{
              sx: { backgroundColor: "#1e1e1e" },
            }}
          />
        )}
      </Box>

      {/* Export Filter Modal */}
      <ExportFilterModal
        open={exportModalOpen}
        onClose={() => setExportModalOpen(false)}
        onExport={handleExport}
        isExporting={isExporting}
        selectedCount={
          Object.keys(rowSelection).filter((key) => rowSelection[key]).length
        }
      />

      {/* Deep Evaluation Modal */}
      <DeepEvaluationModal
        open={evaluationModalOpen}
        onClose={() => {
          setEvaluationModalOpen(false);
          setSelectedEntityForEvaluation(null);
        }}
        entity={selectedEntityForEvaluation}
        criteria={requirements.criteria}
      />

      {/* Export Error Snackbar */}
      <Snackbar
        open={!!exportError}
        autoHideDuration={6000}
        onClose={clearError}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={clearError} severity="error" sx={{ width: "100%" }}>
          {exportError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FinalSelectionView;
